/**
 * ManageRecord Component
 *
 * Overview:
 * The `ManageRecord` component displays detailed information about a selected record,
 * provides various actions (like download and state updates), and includes a result screen
 * for actions like success or error feedback.
 *
 * To Implement:
 * 1. Pass in the necessary `record` data, `currentState`, `role`, and action-related props.
 * 2. Use `onResetActionState` for handling component reset and refreshing the record data
 *    when actions are completed.
 * 3. To use the reusable functions (e.g., `handleBackToRecords` and `resetAndRefetchRecord`),
 *    import them into the parent component to simplify state updates and record refreshing.
 */

import React, { useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { v4 as uuidv4 } from "uuid";
import { httpsCallable } from "firebase/functions";
import { functions } from "../../../firebase";
import logUserAction from "../functions/AuditTrail";
import ActionButton from "./ActionButton";
import LoadingCircle from "../../utils/LoadingCircle";
import ResultScreen from "./ResultScreen";
import { Timestamp } from "firebase/firestore";
import {
  updateSupplierFormData,
  setSupplierLoading,
  setSupplierLoadingLabel,
  setSupplierResult,
} from "../../../redux/actions";
import "./ManageRecord.css";
import { doc, updateDoc, getDoc } from "firebase/firestore";
import { db } from "../../../firebase";

// Format timestamp to a human-readable date
const formatTimestamp = (timestamp) => {
  if (timestamp instanceof Timestamp) {
    const dateObj = timestamp.toDate();
    const day = dateObj.getUTCDate();
    const month = dateObj.toLocaleString("default", { month: "long" });
    const year = dateObj.getUTCFullYear();
    const hours = String(dateObj.getUTCHours()).padStart(2, "0");
    const minutes = String(dateObj.getUTCMinutes()).padStart(2, "0");
    return `${day} ${month} ${year}, ${hours}H${minutes}`;
  } else if (typeof timestamp === "number") {
    return new Date(timestamp).toLocaleString(); // Handles UNIX timestamps as fallback
  }
  return timestamp;
};

const ManageRecord = ({
  record, // Record data to be displayed and managed
  currentState, // The current state of the record (used for action button configurations)
  role, // The role of the current user (used for role-based action button visibility)
  buttonConfig, // Configuration for action buttons based on the record state
  currentUser, // Email or ID of the current user (for logging actions)
  system, // System name for logging and audit trail purposes
  loading, // Boolean indicating if data is loading
  actionStatus, // "success" or "error" status for result screen display
  actionMessage, // Message to display in result screen for success or error
  visible, // Controls visibility of the result screen
  onResetActionState, // Callback function to reset action state after result screen
  onAction, // Callback for custom actions (if needed)
  company, // Company name for database operations
  collectionBrand, // Collection brand for database operations
  systemName, // System name for database operations
  onFieldUpdated, // Callback for field updates
  setIsManageRecordVisible, // Function to set visibility of the ManageRecord component
}) => {
  const dispatch = useDispatch();
  const {
    loading: reduxLoading,
    loadingLabel,
    result,
  } = useSelector((state) => state.supplierForm);

  console.log("Button Config:", buttonConfig);
  const [expandedFields, setExpandedFields] = useState({}); // Tracks expanded/collapsed fields for complex data
  const [editingFields, setEditingFields] = useState({}); // Tracks which fields are being edited
  const [editedValues, setEditedValues] = useState({}); // Stores edited values
  const [isSaving, setIsSaving] = useState(false); // Tracks save operation
  const recordInfoRef = useRef(null); // Ref for record info to capture as PDF

  // Define dropdown options for specific fields
  const dropdownOptions = {
    status: ["Active", "Inactive", "Pending", "Revoked", "Suspended"],
    auditStatus: ["Approved", "Rejected", "Pending", "Under Review"],
  };

  /**
   * Toggles the expansion for a bulk data field (e.g., array or object).
   */
  const handleExpandBulkData = (field) => {
    setExpandedFields((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  /**
   * Toggles edit mode for a specific field
   */
  const handleEditField = (field) => {
    setEditingFields((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));

    // Initialize edited value if not already set
    if (!editedValues[field]) {
      setEditedValues((prev) => ({
        ...prev,
        [field]: record[field] || "",
      }));
    }
  };

  /**
   * Handles saving a field's edited value using Redux and cloud functions
   */
  const handleSaveField = async (field) => {
    setIsSaving(true);
    dispatch(setSupplierLoading(true));
    dispatch(setSupplierLoadingLabel(`Saving ${field}...`));

    try {
      console.log(`Starting save operation for field: ${field}`);
      console.log(`Edited value:`, editedValues[field]);
      console.log(`Record ID:`, record.id);
      console.log(
        `Company: ${company}, Collection: ${collectionBrand}, System: ${systemName}`
      );

      // Determine the correct database path based on field type
      let updateData = {};
      let fieldPath = field;

      // Handle nested fields based on the record structure
      // if (field === "status") {
      //   fieldPath = "extractedData.dataPoints.status";
      // } else if (field === "auditStatus") {
      //   fieldPath = "auditRiskProfile.auditStatus";
      // } else if (field === "names") {
      //   fieldPath = "extractedData.dataPoints.names";
      // } else if (field === "surname") {
      //   fieldPath = "extractedData.dataPoints.surname";
      // } else if (field === "email") {
      //   fieldPath = "extractedData.dataPoints.email";
      // } else if (field === "contactNumber") {
      //   fieldPath = "extractedData.dataPoints.contactNumber";
      // } else if (field === "bank") {
      //   fieldPath = "extractedData.dataPoints.bank";
      // } else if (field === "bankAccountNumber") {
      //   fieldPath = "extractedData.dataPoints.bankAccountNumber";
      // }

      updateData[fieldPath] = editedValues[field];

      console.log(`Field path: ${fieldPath}`);
      console.log(`Update data:`, updateData);

      // Update Redux state first
      dispatch(updateSupplierFormData(field, editedValues[field]));

      // Use cloud function to update the database (bypasses client-side permissions)
      const firestoreOperations = httpsCallable(
        functions,
        "firestoreOperations"
      );

      const cloudFunctionPayload = {
        operation: "update",
        company,
        collectionBrand,
        systemName,
        uniqueId: record.id,
        documentData: updateData,
        auth: true, // Indicate this is an authenticated request
      };

      console.log(`Calling cloud function with payload:`, cloudFunctionPayload);

      const result = await firestoreOperations(cloudFunctionPayload);
      console.log(`Cloud function result:`, result);

      // Log the edit action (optional - handle permission errors gracefully)
      try {
        const timestamp = new Date().toISOString();
        const actionData = {
          system,
          action: "edit record field",
          target: `${record.name || "N/A"} - ${field}`,
          timestamp,
          uniqueId: uuidv4(),
        };

        // await logUserAction(
        //   currentUser,
        //   system,
        //   actionData.action,
        //   actionData.target,
        //   actionData.timestamp,
        //   actionData.uniqueId
        // );
        console.log("Audit trail updated successfully");
      } catch (auditError) {
        // Silently handle audit trail errors - they shouldn't break the main operation
        console.warn(
          "Audit trail update failed (non-critical):",
          auditError.message
        );
        // Don't fail the entire operation if audit trail fails
      }

      // Exit edit mode
      setEditingFields((prev) => ({
        ...prev,
        [field]: false,
      }));

      // Show success message in Redux
      dispatch(
        setSupplierResult({
          status: "success",
          title: `${field} updated successfully`,
          description: `The ${field} field has been updated in the database.`,
        })
      );

      // Call the field update callback to refresh parent component
      if (onFieldUpdated) {
        onFieldUpdated({ field, value: editedValues[field] });
      }

      console.log(`Field ${field} updated successfully`);
    } catch (error) {
      console.error("Error saving field:", error);
      console.error("Error details:", {
        message: error.message,
        code: error.code,
        details: error.details,
        data: error.data,
        stack: error.stack,
      });

      // Show error message in Redux
      dispatch(
        setSupplierResult({
          status: "error",
          title: "Error updating field",
          description: `Failed to update ${field}. Error: ${error.message}`,
        })
      );
    } finally {
      setIsSaving(false);
      dispatch(setSupplierLoading(false));
      dispatch(setSupplierLoadingLabel(""));

      // Clear result after 3 seconds
      setTimeout(() => {
        dispatch(setSupplierResult(null));
      }, 3000);
      window.location.reload(); // Refresh the page to reflect changes
    }
  };

  /**
   * Handles input change for editable fields
   */
  const handleInputChange = (field, value) => {
    setEditedValues((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  /**
   * Renders the appropriate input component based on field type
   */
  const renderInputComponent = (field, value) => {
    if (dropdownOptions[field]) {
      return (
        <select
          value={value || ""}
          onChange={(e) => handleInputChange(field, e.target.value)}
          className="edit-field-input"
        >
          <option value="">Select {field}</option>
          {dropdownOptions[field].map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      );
    }

    return (
      <input
        type="text"
        value={value || ""}
        onChange={(e) => handleInputChange(field, e.target.value)}
        className="edit-field-input"
      />
    );
  };

  /**
   * Generates a downloadable PDF of the record information.
   */
  const handleDownloadPdf = () => {
    const el = document.querySelector(
      "#fee-summary, .ld-fee-summary, #print-root"
    );
    if (!el) return alert("Nothing to print.");

    // Create a new window for printing
    const printWindow = window.open("", "_blank");
    if (!printWindow) {
      alert("Please allow popups to print this document.");
      return;
    }

    // Write the element content to the new window
    printWindow.document.write(`
      <html>
        <head>
          <title>Print Record</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
            table { width: 100%; border-collapse: collapse; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            th { background-color: #f2f2f2; }
          </style>
        </head>
        <body>
          ${el.innerHTML}
        </body>
      </html>
    `);

    printWindow.document.close();
    printWindow.focus();

    // Wait for content to load then print
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 500);
  };

  /**
   * Generates a downloadable CSV of the record information.
   */
  const handleDownloadCSV = () => {
    const csvRows = ["Key,Value"];
    for (const [key, value] of Object.entries(record)) {
      if (Array.isArray(value) || typeof value === "object") {
        csvRows.push(`${key},${JSON.stringify(value).replace(/,/g, ";")}`);
      } else {
        csvRows.push(`${key},${value}`);
      }
    }
    const csvContent = csvRows.join("\n");
    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${record.name || "record"}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  /**
   * Filters and returns the action buttons that should be visible based on the current state and user role.
   */
  const getButtonsForState = () => {
    return buttonConfig.find((config) => config.state === "active").buttons;
  };

  /**
   * Renders action buttons for the current state, applying role-based visibility.
   */
  const renderActionButtons = () => {
    const buttonsForState = getButtonsForState();
    // Filter out the "Edit Record" button since we're making fields editable inline

    const filteredButtons = buttonsForState.filter((button) => {
      return button.allowedRoles.includes(role);
    });
    // .filter((button) => button.label !== "Edit Record");

    console.log(
      "This is the log youre looking ofr:",
      buttonsForState[0]?.allowedRoles + "   " + role
    );
    return filteredButtons?.map((button, index) => (
      <ActionButton
        key={index}
        label={button.label}
        dataAction={button.label === "Edit Record" ? "edit" : undefined}
        onClick={
          button.action
            ? button.action
            : () => console.warn(`${button.label} action is undefined`)
        } // Safeguard
      />
    ));
  };

  /**
   * Determines if a value represents complex bulk data (array or object).
   */
  const isBulkData = (value) =>
    Array.isArray(value) || typeof value === "object";

  /**
   * Renders a complex bulk data field with expand/collapse control.
   */
  const renderBulkData = (key, value) => {
    const isExpanded = expandedFields[key];
    const isArraySimple =
      Array.isArray(value) && value.every((item) => typeof item !== "object");

    return (
      <tr key={key}>
        {/* <td className="bulk-data-expand-button">
          <button
            onClick={() => handleExpandBulkData(key)}
            className="expand-button"
          >
            {isExpanded ? `Collapse ${key}` : `Expand ${key}`}
          </button>
        </td> */}
        <td className="bulk-data-content">
          {isExpanded && (
            <div className="bulk-data-container">
              {isArraySimple
                ? renderSimpleArrayTable(value)
                : renderComplexArrayTables(value)}
            </div>
          )}
        </td>
      </tr>
    );
  };

  /**
   * Renders a simple array as a table with a single column.
   */
  const renderSimpleArrayTable = (arrayData) => (
    <table className="simple-array-table">
      <thead>
        <tr>
          <th>Data</th>
        </tr>
      </thead>
      <tbody>
        {arrayData.map((item, index) => (
          <tr key={index}>
            <td>{item}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );

  /**
   * Renders complex array elements as separate tables, each with their own rows and columns.
   */
  const renderComplexArrayTables = (arrayData) => {
    const timestampRegex = /time[-_]?[sS]tamp/i;

    const sortedArrayData = arrayData.sort((a, b) => {
      const aTimestamp =
        Object.keys(a).find((key) => timestampRegex.test(key)) &&
        new Date(a[Object.keys(a).find((key) => timestampRegex.test(key))]);
      const bTimestamp =
        Object.keys(b).find((key) => timestampRegex.test(key)) &&
        new Date(b[Object.keys(b).find((key) => timestampRegex.test(key))]);

      if (aTimestamp && bTimestamp) {
        return bTimestamp - aTimestamp;
      }
      return 0;
    });

    return (
      <>
        {sortedArrayData.map((item, index) => {
          const sortedKeys = Object.keys(item).sort();

          return (
            <table key={index} className="complex-array-table">
              <thead>
                <tr>
                  <th>Key</th>
                  <th>Value</th>
                </tr>
              </thead>
              <tbody>
                {sortedKeys.map((key) => {
                  const value = item[key];
                  const isTimestamp = timestampRegex.test(key);

                  return (
                    <tr key={key}>
                      <td>{key}</td>
                      <td>
                        {isTimestamp && value
                          ? `${formatTimestamp(value)} (${value})`
                          : JSON.stringify(value) || "N/A"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          );
        })}
      </>
    );
  };

  /**
   * Dynamically renders record information as a table, separating simple and bulk data.
   */
  const renderRecordInformation = () => {
    const simpleData = {};
    const bulkData = {};

    // Fields to exclude from display
    const excludedFields = [
      "auditRiskProfile",
      "extractedData",
      "idDocumentDocument",
      "proofOfBankAccountDocument",
    ];

    Object.entries(record).forEach(([key, value]) => {
      // Skip excluded fields
      if (excludedFields.includes(key)) {
        return;
      }

      const isTimestamp = value instanceof Timestamp;
      const isBulkData =
        Array.isArray(value) || (typeof value === "object" && !isTimestamp);

      if (!isBulkData) {
        simpleData[key] = isTimestamp ? formatTimestamp(value) : value;
      } else {
        bulkData[key] = value;
      }
    });

    const sortedSimpleKeys = Object.keys(simpleData).sort();
    const sortedBulkKeys = Object.keys(bulkData).sort();

    return (
      <table className="record-information-table">
        <tbody>
          {sortedSimpleKeys.map((key) => {
            const isEditing = editingFields[key];
            const displayValue = isEditing
              ? editedValues[key]
              : simpleData[key];

            return (
              <tr key={key}>
                <td className="record-key">
                  <strong>{key}</strong>
                </td>
                <td className="record-value">
                  {isEditing ? (
                    <div className="edit-field-container">
                      {renderInputComponent(key, displayValue)}
                      <button
                        onClick={() => handleSaveField(key)}
                        className="save-button"
                        title="Save"
                        disabled={isSaving || reduxLoading}
                      >
                        {isSaving || reduxLoading ? "⏳" : "💾"}
                      </button>
                    </div>
                  ) : (
                    <div className="field-display-container">
                      <span className="field-value">
                        {displayValue || "N/A"}
                      </span>
                      <button
                        onClick={() => handleEditField(key)}
                        className="edit-button"
                        title="Edit"
                      >
                        ✏️
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            );
          })}
          {sortedBulkKeys.map((key) => renderBulkData(key, bulkData[key]))}
        </tbody>
      </table>
    );
  };

  // Render loading screen, result screen, or record information screen based on props.
  if (loading || reduxLoading) {
    return <LoadingCircle label={loadingLabel || "Processing..."} />;
  }

  if (visible && actionStatus != null) {
    return (
      <ResultScreen
        type={actionStatus} // "success" or "error"
        message={actionMessage}
        onContinue={onResetActionState}
      />
    );
  }

  // Show Redux result if available
  if (result) {
    return (
      <ResultScreen
        type={result.status} // "success" or "error"
        message={result.description || result.title}
        onContinue={() => dispatch(setSupplierResult(null))}
      />
    );
  }

  const deleteRecord = async () => {
    console.log("Deleting current record:");
    try {
      const firestoreOperations = httpsCallable(
        functions,
        "firestoreOperations"
      );

      const payload = {
        operation: "delete",
        company,
        collectionBrand,
        systemName,
        uniqueId: record.id,
        auth: true,
      };

      console.log("Deleting current record:" + JSON.stringify(payload));

      const result = await firestoreOperations(payload);

      if (result.data) {
        console.log("Document exists:", result.data);
        alert("Record deleted successfully!");
        setIsManageRecordVisible(false);

        // window.location.reload();
      } else {
        alert(
          "No record found with the provided ID. Please check the ID and try again."
        );
      }
    } catch (error) {
      alert(`failed: ${error.message}`);
    }
  };

  return (
    <div className="manage-record-container">
      <div className="manage-header">
        <h1>{record.systemName || "Record"} Management</h1>
        <h2>
          Selected Record:{" "}
          <span className="highlight-gold">{record.name || "N/A"}</span>
        </h2>
        <p>
          Record Status: <span className="highlight-gold">{currentState}</span>
        </p>
      </div>

      <div className="manage-content-layout">
        <div className="left-column">
          <div className="action-section">
            <h3 className="action-title">Actions</h3>
            <div className="action-buttons">{renderActionButtons()}</div>

            {/* Debug button for testing database connection */}
            <button
              onClick={deleteRecord}
              style={{
                marginTop: "10px",
                padding: "8px 16px",
                backgroundColor: "#ff6b6b",
                color: "white",
                border: "none",
                borderRadius: "4px",
                cursor: "pointer",
                fontSize: "12px",
              }}
              disabled={
                role === "Exco" || role === "Management" || role === "CFO"
                  ? false
                  : true
              }
            >
              Delete
            </button>
          </div>

          <div className="download-section">
            <h3 className="download-title">Download Record</h3>
            <button className="download-button" onClick={handleDownloadPdf}>
              Download PDF
            </button>
            <button className="download-button" onClick={handleDownloadCSV}>
              Download CSV
            </button>
          </div>
        </div>

        <div className="right-column">
          <div className="record-information" ref={recordInfoRef}>
            <h3>Record Information</h3>
            {renderRecordInformation()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ManageRecord;
